'use client'

import { useCallback, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import { Tab, TabSelect } from '@/components/TabSelect'
import { useBills } from '@/hooks/useBills'
import { useLegislativeContext } from '@/providers/LegislativeProvider'
import { billStateCodeToScopeSlug } from '@/utilities/billTrack50/billRouting'
import { cn } from '@/utilities/ui'

import { ActivityContent } from './components/ActivityContent'
import { RelatedContent } from './components/RelatedContent'
import { VoterVoiceContent } from './components/VoterVoiceContent'

import type { Bill, Topic } from '@/payload-types'

export const BillSidebar = ({ bill, billOver }: { bill: Bill; billOver: boolean }) => {
  const { billTopics: allBillTopics } = useLegislativeContext()

  const billActions = bill?.bt50?.actions ?? []
  const scope = billStateCodeToScopeSlug(bill.bt50?.stateCode)
  const voterVoiceUrl = bill?.voterVoiceUrl

  const [selectedTab, setSelectedTab] = useState<Tab['value']>(
    voterVoiceUrl && !billOver ? 'take-action' : 'activity',
  )

  const topics = bill?.topics?.filter(
    (topic): topic is Topic => typeof topic === 'object' && topic !== null,
  )

  const topicIds = useMemo(
    () => topics?.map((topic) => topic.id) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bill.id],
  )

  // Fetch related bills from API — with topic matching
  const { bills: topicMatches } = useBills({
    limit: 5,
    sort: '-bt50.actionDate',
    scope,
    topics: topicIds.length > 0 ? topicIds : undefined,
  })

  // Fetch scope-only fallback (no topic filter)
  const { bills: scopeMatches } = useBills({
    limit: 5,
    sort: '-bt50.actionDate',
    scope,
  })

  const relatedBills = useMemo(() => {
    const exclude = (list: Bill[]) => list.filter((b) => b.id !== bill.id).slice(0, 4)
    const byTopic = exclude(topicMatches)
    return byTopic.length > 0 ? byTopic : exclude(scopeMatches)
  }, [topicMatches, scopeMatches, bill.id])

  const billSidebarTabs: Tab[] = [
    !billOver && voterVoiceUrl && { name: 'Take Action', value: 'take-action' },
    { name: 'Activity', value: 'activity' },
    relatedBills.length > 0 && { name: 'Related Bills', value: 'related-bills' },
  ].filter(Boolean) as Tab[]

  const handleTabChange = useCallback(
    (tab: Tab['value']) => {
      setSelectedTab(tab as Tab['value'])
    },
    [setSelectedTab],
  )

  return (
    <div
      className={cn(
        'sticky top-21 flex w-full flex-col self-start',
        selectedTab === 'take-action' ? 'h-300' : 'h-100 lg:h-225',
      )}
    >
      <div className="flex h-full min-h-0 flex-1 flex-col">
        {billSidebarTabs.length > 1 ? (
          <TabSelect
            size="small"
            tabs={billSidebarTabs}
            value={selectedTab}
            onTabChange={handleTabChange}
            className="shrink-0"
          />
        ) : (
          <h3 className="font-family-body text-base font-semibold text-muted-foreground">
            Activity
          </h3>
        )}
        <div
          className={cn(
            'mt-6 min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]',
            selectedTab === 'take-action' &&
              'flex flex-col overflow-hidden rounded-2xl border border-border bg-neutral-50',
          )}
        >
          <AnimatePresence>
            {selectedTab === 'activity' && billActions && billActions.length > 0 && (
              <motion.div
                key="activity"
                initial={{ opacity: 0, y: -10, position: 'absolute' }}
                animate={{
                  opacity: 1,
                  y: 0,
                  position: 'relative',
                  transition: { duration: 0.2, ease: 'easeOut', delay: 0.2 },
                }}
                exit={{ opacity: 0, y: 10, position: 'absolute' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ActivityContent billActions={billActions} />
              </motion.div>
            )}
            {selectedTab === 'related-bills' && relatedBills && relatedBills.length > 0 && (
              <motion.div
                key="related-bills"
                initial={{ opacity: 0, y: -10, position: 'absolute' }}
                animate={{
                  opacity: 1,
                  y: 0,
                  position: 'relative',
                  transition: { duration: 0.2, ease: 'easeOut', delay: 0.2 },
                }}
                exit={{ opacity: 0, y: -10, position: 'absolute' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <RelatedContent relatedBills={relatedBills} allBillTopics={allBillTopics} />
              </motion.div>
            )}
            {selectedTab === 'take-action' && voterVoiceUrl && (
              <motion.div
                key="take-action"
                initial={{ opacity: 0, y: -10, position: 'absolute' }}
                animate={{
                  opacity: 1,
                  y: 0,
                  position: 'relative',
                  transition: { duration: 0.2, ease: 'easeOut', delay: 0.2 },
                }}
                exit={{ opacity: 0, y: -10, position: 'absolute' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="flex min-h-0 w-full flex-1 flex-col"
              >
                <VoterVoiceContent voterVoiceUrl={voterVoiceUrl} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
